import gql from 'graphql-tag';
import React, {useMemo} from 'react';

import {assert} from 'Asserts';
import {
  CachedDataWhenRefetchingNamedOption,
  QueryResultType,
  useBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {WorksheetContext} from 'notebook/worksheets/WorksheetContext';
import notifications from 'notifications';
import {useNullableMethodEditorContext} from 'procedures/design/method-config/MethodEditorContext';
import {ProcedureMetadata} from 'procedures/ProcedureDomainTypes';
import {useNullableProcedurePageContext} from 'procedures/ProcedurePageContext';
import {PlateMapProcedureContext} from 'samples/plate-maps/PlateMapConstants';
import APIUtil from 'util/APIUtil';

import {
  ParametersForPlateMapInConfigQuery,
  ParametersForPlateMapInConfigQueryVariables,
} from './__generated/ParametersForPlateMapInConfigQuery';
import {
  ParametersForPlateMapInExecutionQuery,
  ParametersForPlateMapInExecutionQueryVariables,
} from './__generated/ParametersForPlateMapInExecutionQuery';
import {transformConfigQueryData, transformExecutionQueryData} from './ParametersForPlateMapTransforms';

type WrapperChild = ({
  isInProcedure,
  plateDataParameters,
  plateMaterialInputs,
  procedureStudyDesignType,
  procedureId,
  procedureTestOrderConfig,
  procedureMethodExecutionInstanceId,
}: PlateMapProcedureContext & {procedureMethodExecutionInstanceId: string | null}) => JSX.Element;

export const PARAMETERS_FOR_PLATE_MAP_IN_EXECUTION_QUERY = gql`
  query ParametersForPlateMapInExecutionQuery($stepExecutionInstanceId: StrictString!)
  @skipAudit
  @engComponent(name: PROCEDURES) {
    procedureStepExecutionInstance(id: $stepExecutionInstanceId) {
      ... on ProcedureStepExecutionInstanceNode {
        id
        methodExecutionInstance {
          id
          parameterUsagesExpensive {
            stepExecutionInstanceId
            parameterVersionId
          }
          parameterConfirmationMethods {
            parameterId
            confirmationMethod
          }
          procedureRun {
            id
            study {
              ... on StudyNode {
                id
                studyDesign {
                  id
                  type
                }
              }
              ... on InaccessibleInfoNode {
                ...InaccessibleInfoGraphQL
              }
            }
            templateProcedureFlowchartVersion {
              ... on ProcedureFlowchartVersionNode {
                id
                procedureFlowchart {
                  id
                  parent {
                    id
                  }
                }
              }
              ... on InaccessibleInfoNode {
                ...InaccessibleInfoGraphQL
              }
            }
            procedureFlowchartConfigVersion {
              ... on ProcedureFlowchartConfigVersionNode {
                id
                procedure {
                  id
                }
              }
              ... on InaccessibleInfoNode {
                ...InaccessibleInfoGraphQL
              }
            }
          }
          ...ProcedureMethodExecutionInstancePlannedValuesGraphQL
          planMethodDefinitionVersion {
            ... on ProcedurePlanMethodDefinitionVersionNode {
              id
              dataParameterSettings {
                version {
                  id
                  name
                  restValueField
                  procedureDataParameterId
                }
              }
              materialInputSettings {
                version {
                  id
                  name
                  restMaterialField
                  procedureMaterialInputId
                }
              }
              testDefinitions {
                ... on TestDefinitionNode {
                  id
                }
                ...InaccessibleInfoGraphQL
              }
              dilutionPlan {
                allowedDilutionFactors
              }
            }
            ...InaccessibleInfoGraphQL
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ProcedureMethodExecutionInstancePlannedValues').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

const PROCEDURE_METHOD_DEFINITION_VERSION_FOR_PLATE_MAP_FRAGMENT = gql`
  fragment ProcedureMethodDefinitionVersionForPlateMapGraphQL on ProcedureMethodDefinitionVersionNode {
    id
    dataParameterSettings {
      version {
        id
        name
        restValueField
        confirmationMethod
      }
      values {
        id
        value
      }
    }
    materialInputSettings {
      version {
        id
        name
        restMaterialField
        confirmationMethod
      }
      values {
        id
        material
      }
    }
    testDefinitions {
      ... on TestDefinitionNode {
        id
      }
      ...InaccessibleInfoGraphQL
    }
    dilutionPlan {
      allowedDilutionFactors
    }
    parameterUsagesExpensive {
      stepId
      parameterVersionId
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

// This query is used during config, so it needs to use the /latest/ flowchart version (not /published/)
export const PARAMETERS_FOR_PLATE_MAP_IN_CONFIG_QUERY = gql`
  query ParametersForPlateMapInConfigQuery($procedureId: StrictString!)
  @skipAudit
  @engComponent(name: PROCEDURES) {
    procedure(id: $procedureId) {
      ... on ProcedureNode {
        id
        procedureFlowchart {
          id
          latestFlowchartVersion {
            ... on ProcedureFlowchartVersionNode {
              id
              nodes {
                ...ProcedureMethodDefinitionVersionForPlateMapGraphQL
              }
            }
            ...InaccessibleInfoGraphQL
          }
        }
        latestProcedureFlowchartConfigVersion {
          id
          nodes {
            ...ProcedureMethodDefinitionVersionForPlateMapGraphQL
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${PROCEDURE_METHOD_DEFINITION_VERSION_FOR_PLATE_MAP_FRAGMENT}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * This subcomponent handles the procedure execution worksheet scenario.
 */
function ParametersForPlateMapInExecutionWrapper(props: {
  deprecatedStepCreationContextId: string;
  children: WrapperChild;
}): JSX.Element | null {
  const queryResult = useBenchlingQuery<
    ParametersForPlateMapInExecutionQuery,
    ParametersForPlateMapInExecutionQueryVariables
  >(PARAMETERS_FOR_PLATE_MAP_IN_EXECUTION_QUERY, {
    variables: {stepExecutionInstanceId: props.deprecatedStepCreationContextId},
  });

  // Guarantee referential integrity when nothing changes. Some children use them as memo/effect dependencies.
  const transformResult = useMemo(() => {
    if (queryResult.type !== QueryResultType.SUCCESS) {
      return null;
    }
    return transformExecutionQueryData(queryResult.data, props.deprecatedStepCreationContextId);
  }, [queryResult, props.deprecatedStepCreationContextId]);

  if (queryResult.type === QueryResultType.PENDING) {
    return (
      <>
        {props.children({
          isInProcedure: true,
          procedureStudyDesignType: null,
          procedureId: null,
          procedureMethodExecutionInstanceId: null,
        })}
      </>
    );
  }
  if (queryResult.type === QueryResultType.ERROR) {
    notifications.error('Could not load parameter information; try reloading the page.');
    return null;
  }

  return <>{props.children(transformResult!)}</>;
}

function ParametersForPlateMapInConfigWrapper(props: {
  procedureId: string;
  methodVersionId: string;
  stepVersionId: string;
  children: WrapperChild;
}): JSX.Element | null {
  const {procedureId, methodVersionId, stepVersionId} = props;
  const queryResult = useBenchlingQuery<
    ParametersForPlateMapInConfigQuery,
    ParametersForPlateMapInConfigQueryVariables
  >(PARAMETERS_FOR_PLATE_MAP_IN_CONFIG_QUERY, {
    variables: {procedureId},
    fetchPolicy: 'cache-and-network',
    renderCachedDataWhenRefetching: CachedDataWhenRefetchingNamedOption.WHEN_LOADING,
  });

  // Guarantee referential integrity when nothing changes. Some children use them as memo/effect dependencies.
  const transformResult = useMemo(() => {
    if (queryResult.type !== QueryResultType.SUCCESS) {
      return null;
    }
    return transformConfigQueryData(queryResult.data, methodVersionId, stepVersionId);
  }, [methodVersionId, queryResult, stepVersionId]);

  if (queryResult.type === QueryResultType.PENDING) {
    return (
      <>
        {props.children({
          isInProcedure: true,
          procedureStudyDesignType: null,
          procedureId: null,
          procedureMethodExecutionInstanceId: null,
        })}
      </>
    );
  }
  if (queryResult.type === QueryResultType.ERROR) {
    notifications.error('Could not load parameter information; try reloading the page.');
    return null;
  }

  if (transformResult == null) {
    return (
      <>
        {props.children({
          isInProcedure: true,
          plateDataParameters: [],
          plateMaterialInputs: [],
          procedureStudyDesignType: null,
          procedureId,
          procedureTestOrderConfig: null,
          procedureMethodExecutionInstanceId: null,
        })}
      </>
    );
  }

  return <>{props.children(transformResult)}</>;
}

/**
 * This wrapper is used within the context of a worksheet and provides its child with procedure parameter
 * information in the form of PlateDataParameters. It can be safely used outside of a procedure step document,
 * where it gracefully passes down an empty list to represent "no parameters found".
 *
 * This encapsulates all procedures domain-specific logic to minimize mixing procedures concerns into general
 * worksheet / plate map components.
 */
export default function ParametersForPlateMapWrapper(props: {children: WrapperChild}): JSX.Element | null {
  const {deprecatedStepCreationContextId} = WorksheetContext.useContext();
  const procedurePageContext: {
    procedureMetadata: ProcedureMetadata;
  } | null = useNullableProcedurePageContext();
  const methodEditorContext = useNullableMethodEditorContext();

  const isInProcedureExecution =
    deprecatedStepCreationContextId != null &&
    APIUtil.isProcedureStepExecutionInstance(deprecatedStepCreationContextId);
  const isInProcedureConfig = procedurePageContext != null && methodEditorContext != null;
  assert(
    !isInProcedureExecution || !isInProcedureConfig,
    'Procedure config and execution are mutually exclusive contexts'
  );

  if (isInProcedureExecution) {
    return (
      <ParametersForPlateMapInExecutionWrapper
        deprecatedStepCreationContextId={deprecatedStepCreationContextId}
      >
        {props.children}
      </ParametersForPlateMapInExecutionWrapper>
    );
  }

  if (isInProcedureConfig) {
    const {procedureMetadata} = procedurePageContext;
    const {currentMethodId, selectedStepCid, stepCidToIdMap} = methodEditorContext;
    const stepVersionId = selectedStepCid != null ? stepCidToIdMap[selectedStepCid] : null;

    if (
      currentMethodId != null &&
      selectedStepCid != null &&
      // stepVersionId could theoretically be null if the step is not yet persisted, but in practice, we save
      // immediately when a new step is created, so we shouldn't hit this case
      stepVersionId != null
    ) {
      return (
        <ParametersForPlateMapInConfigWrapper
          procedureId={procedureMetadata.id}
          methodVersionId={currentMethodId}
          stepVersionId={stepVersionId}
          key={currentMethodId} // Force re-query of latest parameters when method version changes
        >
          {props.children}
        </ParametersForPlateMapInConfigWrapper>
      );
    }
  }

  return (
    <>
      {props.children({
        isInProcedure: false,
        procedureStudyDesignType: null,
        procedureId: null,
        procedureMethodExecutionInstanceId: null,
      })}
    </>
  );
}
